import { Capacitor } from '@capacitor/core';
import { DateTime } from 'luxon';
import { suggestNewTripTitle } from '@deckee/utilities';
import { callEndpoint, telemetry } from '../backend';
import { captureEvent } from '../event-capture';
import { hasFeatureEnabled } from '../feature';
import { geoLocationGetCurrentPosition, updateCurrentPosition } from '../geo';
import checkCapacitorAndPluginsAvailable from '../helper-functions';
import { insomniaAllowSleep, insomniaKeepAwake } from '../insomnia';
import { get, set } from '../local-storage';
import {
  calculateCompassHeading,
  startWatchingOrientation,
  stopWatchingOrientation,
} from '../orientation';
import {
  getPhysicalActivityPermissionStatus,
  isLocationEnabled,
  makePhysicalActivityPermissionRequest,
  requestLocationPermission,
} from '../permissions';
import {
  ROUTE_FLOAT_PLAN_ACTIVATE,
  ROUTE_LOCATION_DISCLOSURE,
  ROUTE_TRIP_ACTIVE,
  ROUTE_TRIP_RESUME,
} from '../route/route-names';
import {
  getUrlForRouteWithGivenParameters,
  transitionToNextRoute,
} from '../route/route-navigation';
import captureSpecificError from '../sentry';
import { getStore } from '../store';
import {
  hideControlPaneReset,
  moveMapToLocation,
  showControlPaneReset,
} from '../store/modules/app-controls';
import { updateCurrentHeading } from '../store/modules/geo-service';
import { actions, setFollowModeActive } from '../store/modules/log-trips';
import {
  backgroundGeolocationRequestPermission,
  backgroundGeolocationStart,
  backgroundGeolocationStop,
  isBackgroundGeolocationDisclosed,
  resetCurrentTrip,
  updateBackgroundGeolocationConfiguration,
} from './background-geolocation';

let previousHeading;

export const checkPermissionsAndUpdateCurrentPosition = async () => {
  let locationPermissionStatus = true;
  // This is for requesting location permissions
  // on mobile devices
  try {
    /* eslint-disable no-undef */
    const nativePlatform = Capacitor.isNativePlatform();
    if (nativePlatform) {
      // Get location permission status and request if needed
      const locationPermission = await requestLocationPermission();
      // Check if location services is available
      const isLocationServicesEnabled = await isLocationEnabled();
      if (
        locationPermission ===
          cordova.plugins.diagnostic.permissionStatus.DENIED ||
        locationPermission ===
          cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS ||
        !isLocationServicesEnabled
      ) {
        // Check if still disabled
        locationPermissionStatus = false;
      }
      /* eslint-enable no-undef */
    }
  } catch (error) {
    // User on browser, disregard
  }

  // Trigger location permissions dialog (in browser) by requesting location.
  // When you'll receive PositionError with PERMISSION_DENIED constant code,
  // it'll mean that user denied location permission (just now or at app settings).
  // https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError/code
  if (locationPermissionStatus) {
    geoLocationGetCurrentPosition({
      successCallback: () => {
        /* no op */
      },
      errorCallback: async (positionError) => {
        // Denied or not available
        if (positionError.code !== 3) {
          throw new Error('Location services are not enabled.');
        } else {
          // Show the my location popover summary
          updateCurrentPosition();
        }
      },
      options: { timeout: 0 },
    });
  }
};

// Device orientation handler
export const updateBearing = (data) => {
  // Can't get components of device orientation, ignore
  if (!(data?.alpha && data?.beta && data?.gamma)) return;
  const currentHeading = calculateCompassHeading(
    data?.alpha,
    data?.beta,
    data?.gamma,
  );
  // Orientation hasn't changed, ignore
  if (previousHeading === currentHeading) return;
  previousHeading = currentHeading;
  try {
    const { followModeActive, tripPaused } = getStore().getState().logTrips;
    if (followModeActive && !tripPaused) {
      getStore().dispatch(
        moveMapToLocation({
          bearing: currentHeading,
        }),
      );
    }
  } catch (error) {
    const telemetryMessage = `[orientation.updateBearing] Failed to update heading ${error.message}`;
    // eslint-disable-next-line no-bitwise
    telemetry(telemetry.ERROR, telemetryMessage);
    captureSpecificError(
      telemetryMessage,
      'containers/app-main-client/src/services/log-trips/index.js',
    );
  }

  getStore().dispatch(updateCurrentHeading(currentHeading));
};

/**
 * Starts trip by:
 * 1. Checking plugin is available
 * 2. Starts trip in backend with current position
 * 3. Starts tracking geolocation
 * 4. Updates redux state
 */
export const startTrip = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async ({ screenAutolock }) => {
    // This will be looked at in the trips 2.0 accuracy
    // This is for requesting Physical Activity permissions HIGHLY NEEDED for quality
    // eslint-disable-next-line no-warning-comments
    // TODO: Add some sort of front-end error or dialog to handle when this is denied

    const physicalActivityPermissionStatus =
      await getPhysicalActivityPermissionStatus();
    if (!physicalActivityPermissionStatus) {
      await makePhysicalActivityPermissionRequest();
    }

    // Get the trip start time, zone and offset
    const startTimestamp = DateTime.local().toUTC().toISO();
    const startTimeTimezone = DateTime.local().toFormat('z');
    const startTimeUtcOffset = +DateTime.local().toFormat('Z');

    const { currentPosition } = getStore().getState().geoService;

    // This will be looked at in the trips 2.0 accuracy
    // eslint-disable-next-line no-warning-comments
    // TODO: Check if current position would be better off being retrieved from plugin first
    const newTrip = await callEndpoint(callEndpoint.endpoints.startTrip, {
      startTimestamp,
      startTimeTimezone,
      startTimeUtcOffset,
      latLon: currentPosition,
    });

    // Start using the device's magnetometer for heading
    if (hasFeatureEnabled('device-orientation')) {
      await startWatchingOrientation(updateBearing);
    }

    resetCurrentTrip();

    telemetry(
      telemetry.DEBUG,
      `[logTrips.startTrip] Received created trip: ${JSON.stringify(newTrip)}`,
    );

    await backgroundGeolocationStart({
      tripID: newTrip && newTrip.trip && newTrip.trip.id,
      lowFrequencyTracking: false,
      force: true,
    });

    const captureEventPromise = captureEvent({
      type: 'trip: start',
      payload: {
        tripType: 'trip only',
      },
      sendToAmplitude: true,
    });

    if (hasFeatureEnabled('insomnia-autolock') && window.plugins.insomnia) {
      try {
        if (!screenAutolock) {
          insomniaKeepAwake();
        }
      } catch (insomniaError) {
        captureSpecificError(
          insomniaError,
          'containers/app-main-client/src/services/log-trips/index.js',
        );
        telemetry(
          telemetry.ERROR,
          `[logTrips.newTrip] Error: ${insomniaError}`,
        );
      }
    }

    getStore().dispatch(
      actions.setCurrentTripID(newTrip && newTrip.trip && newTrip.trip.id),
    );
    getStore().dispatch(actions.setCurrentTripStartTime(startTimestamp));
    getStore().dispatch(actions.setFollowModeActive());
    getStore().dispatch(actions.clearActiveTripHidden());
    getStore().dispatch(actions.setActiveTrip());

    await captureEventPromise;

    return newTrip.trip;
  },
);

export const pauseTrip = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async () => {
    const { currentTripID, activeTrip, tripPaused } =
      getStore().getState().logTrips;

    // No trip to pause or already paused
    if (!currentTripID || !activeTrip || tripPaused) return false;

    await callEndpoint(callEndpoint.endpoints.pauseResumeTrip, {
      tripID: currentTripID,
      eventType: 'pause',
      timestamp: DateTime.local().toUTC().toISO(),
    });

    getStore().dispatch(actions.setPauseTrip());
    await backgroundGeolocationStop();
    await captureEvent({
      type: 'trip: stop',
      payload: {},
      sendToAmplitude: true,
    });
    return true;
  },
);

const resumeTripLoggedOnState = async (tripID) => {
  const { currentTripPlanID } = getStore().getState().logTrips;
  if (currentTripPlanID) {
    getStore().dispatch(actions.setTripLoggedOn());
    getStore().dispatch(actions.setCurrentTripPlanID(currentTripPlanID));
  } else {
    const response = await callEndpoint(
      callEndpoint.endpoints.getActiveTripPlans,
      {
        tripID,
      },
    );
    const { activeTripPlans } = response;
    if (
      activeTripPlans &&
      Array.isArray(activeTripPlans) &&
      activeTripPlans.length > 0
    ) {
      getStore().dispatch(actions.setTripLoggedOn());
      getStore().dispatch(
        actions.setCurrentTripPlanID(activeTripPlans?.[0]?.id),
      );
    }
  }
};

export const loadRemoteTripIntoActiveTrip = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async (tripID) => {
    const tripDetails = await callEndpoint(
      callEndpoint.endpoints.getTripDetails,
      {
        tripID,
      },
    );

    const currentTripTrackPoints = await get('logTrips', 'currentTrip');
    if (!currentTripTrackPoints || currentTripTrackPoints.length === 0) {
      const tripTrackPoints = await callEndpoint(
        callEndpoint.endpoints.getTripTrackPoints,
        {
          tripID,
        },
      );
      set(
        'logTrips',
        'currentTrip',
        tripTrackPoints?.trackPoints.map((coords) => [
          coords.longitude,
          coords.latitude,
        ]),
      );
    }

    await resumeTripLoggedOnState(tripID);

    getStore().dispatch(
      actions.setCurrentTripStatistics(
        null,
        tripDetails?.trip?.totalDistance || 0,
      ),
    );
    getStore().dispatch(actions.setCurrentTripID(tripID));
    getStore().dispatch(actions.setActiveTrip());
    getStore().dispatch(actions.setFollowModeActive());
    getStore().dispatch(actions.setPauseTrip());
    getStore().dispatch(
      actions.setCurrentTripStartTime(tripDetails?.trip?.startTimestamp),
    );

    // find any attached float-plans and set the trips float-plan token
    const floatPlan = await callEndpoint(
      callEndpoint.endpoints.getFloatPlanByTrip,
      {
        id: tripID,
      },
    );

    if (floatPlan) {
      getStore().dispatch(actions.setTripFloatPlanToken(floatPlan?.shareToken));
    }
  },
);

export const resumeTrip = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async ({ screenAutolock }) => {
    if (hasFeatureEnabled('insomnia-autolock') && window.plugins.insomnia) {
      try {
        if (!screenAutolock) {
          insomniaKeepAwake();
        }
      } catch (insomniaError) {
        captureSpecificError(
          insomniaError,
          'containers/app-main-client/src/services/log-trips/index.js',
        );
        telemetry(
          telemetry.ERROR,
          `[logTrips.resumeTrip] Error: ${insomniaError}`,
        );
      }
    }

    const { currentTripID, tripPaused } = getStore().getState().logTrips;

    if (!currentTripID || !tripPaused) return false;

    await callEndpoint(callEndpoint.endpoints.pauseResumeTrip, {
      tripID: currentTripID,
      eventType: 'resume',
      timestamp: DateTime.local().toUTC().toISO(),
    });

    const resumeTripLoggedOnStatePromise =
      resumeTripLoggedOnState(currentTripID);
    const backgroundGeolocationStartPromise = backgroundGeolocationStart({
      tripID: currentTripID,
      lowFrequencyTracking: false,
      force: true,
    });
    await Promise.all([
      resumeTripLoggedOnStatePromise,
      backgroundGeolocationStartPromise,
    ]);

    getStore().dispatch(actions.clearPauseTrip());
    getStore().dispatch(actions.setActiveTrip());
    getStore().dispatch(actions.setFollowModeActive());
    // Start using the device's magnetometer for heading
    if (hasFeatureEnabled('device-orientation')) {
      await startWatchingOrientation();
    }

    await captureEvent({
      type: 'trip: resume',
      payload: {},
      sendToAmplitude: true,
    });
    return true;
  },
);

export const stopTrip = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async () => {
    const { currentTripID, tripFloatPlanToken } =
      getStore().getState().logTrips;
    if (!currentTripID) return false;
    const stoppedTripID = currentTripID;

    // Get the trip end time, zone and offset
    const endTimestamp = DateTime.local().toUTC().toISO();
    const endTimeTimezone = DateTime.local().toFormat('z');
    const endTimeUtcOffset = +DateTime.local().toFormat('Z');

    const tripTitle = suggestNewTripTitle(endTimestamp, endTimeTimezone);
    const endTripPromise = callEndpoint(callEndpoint.endpoints.stopTrip, {
      endTimestamp,
      endTimeTimezone,
      endTimeUtcOffset,
      tripID: stoppedTripID,
      title: tripTitle,
    });

    getStore().dispatch(actions.setCurrentTripID(null));
    getStore().dispatch(actions.clearActiveTrip());
    getStore().dispatch(actions.clearFollowModeActive());
    getStore().dispatch(actions.clearPauseTrip());
    getStore().dispatch(actions.setCurrentTripStatistics(0, 0));
    getStore().dispatch(actions.setCurrentTripStartTime(null));
    set('logTrips', 'currentTripID', null);
    const geolocationStopPromise = backgroundGeolocationStop();
    // clear the float-plan too as it will automatically end when the trip ends
    if (tripFloatPlanToken) {
      getStore().dispatch(actions.setTripFloatPlanToken(null));
    }

    resetCurrentTrip();

    if (hasFeatureEnabled('insomnia-autolock') && window.plugins.insomnia) {
      try {
        // We always want to allow the screen to sleep again whether they have the setting enabled or not
        insomniaAllowSleep();
      } catch (insomniaError) {
        captureSpecificError(
          insomniaError,
          'containers/app-main-client/src/services/log-trips/index.js',
        );
        telemetry(
          telemetry.ERROR,
          `[logTrips.stopTrip] Error: ${insomniaError}`,
        );
      }
    }

    // Stop using the device's magnetometer for heading
    if (hasFeatureEnabled('device-orientation')) {
      stopWatchingOrientation();
    }
    const endTrip = await endTripPromise;
    await geolocationStopPromise;
    await captureEvent({
      type: 'trip: finish',
      payload: {},
      sendToAmplitude: true,
    });

    telemetry(
      telemetry.DEBUG,
      `[logTrips.stopTrip] Ended trip: ${JSON.stringify(endTrip)}`,
    );
    return stoppedTripID;
  },
);

export const setBackgroundGeolocationDisclosed = (isDisclosed = true) => {
  set('logTrips', 'isBackgroundGeolocationDisclosed', isDisclosed);
};

export const requestPermissions = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async ({ forcePrompt = false }) => {
    try {
      const isDisclosed = await isBackgroundGeolocationDisclosed();
      if (!isDisclosed || forcePrompt) {
        await updateBackgroundGeolocationConfiguration({
          disableLocationAuthorizationAlert: false,
        });
        await backgroundGeolocationRequestPermission();
        setBackgroundGeolocationDisclosed(true);
        await updateBackgroundGeolocationConfiguration({
          disableLocationAuthorizationAlert: true,
        });
      }
    } catch (e) {
      captureEvent({
        type: 'log-trips: request permissions error',
        payload: {
          forcePrompt,
          message: e.message,
        },
      });
      throw e;
    }
  },
);

export const getDisclosureState = isBackgroundGeolocationDisclosed;

export const activateFloatplan = async (type = 'float-plan') => {
  try {
    const disclosureState = await isBackgroundGeolocationDisclosed();
    const [url] = getUrlForRouteWithGivenParameters(ROUTE_FLOAT_PLAN_ACTIVATE, {
      type,
    });

    if (!disclosureState) {
      transitionToNextRoute(ROUTE_LOCATION_DISCLOSURE, { backTo: url });
    } else {
      transitionToNextRoute(ROUTE_FLOAT_PLAN_ACTIVATE, {
        type,
      });
    }
  } catch (error) {
    telemetry(telemetry.DEBUG, `[logTrips.activateFloatplan] Error: ${error}`);
    // TODO: Show some error message that trip could not be started
  }
};

/**
 * Check the disclosure state then route to the active trip pane
 *
 * @returns {void} return nothing
 */
export const activateTripPane = async () => {
  try {
    const disclosureState = await isBackgroundGeolocationDisclosed();

    if (!disclosureState) {
      transitionToNextRoute(ROUTE_LOCATION_DISCLOSURE);
    } else {
      getStore().dispatch(setFollowModeActive());
      transitionToNextRoute(ROUTE_TRIP_ACTIVE);
    }
  } catch (error) {
    telemetry(
      telemetry.DEBUG,
      `[logTrips.isBackgroundGeolocationDisclosed] Error: ${error}`,
    );
    // TODO: Show some error message that trip could not be started
  }
};

export const getTripDetails = async (tripID) => {
  const trip = await callEndpoint(callEndpoint.endpoints.getTripDetails, {
    tripID,
  });
  return trip.trip;
};

export const activateFollowMode = () => {
  // return if already active
  const { followModeActive } = getStore().getState().logTrips;
  if (followModeActive) return false;

  getStore().dispatch(actions.setFollowModeActive());
  getStore().dispatch(hideControlPaneReset());
  return true;
};

export const activateFreeMapMode = () => {
  // return if already in free map mode
  const { followModeActive } = getStore().getState().logTrips;
  if (!followModeActive) return false;

  getStore().dispatch(actions.clearFollowModeActive());
  getStore().dispatch(showControlPaneReset());
  return true;
};

export const initialize = async () => {
  let isDisclosed = false;

  try {
    isDisclosed = (await isBackgroundGeolocationDisclosed()) || false;
  } catch (e) {
    // leave as the default
  }

  setBackgroundGeolocationDisclosed(isDisclosed);

  const currentTripID = (await get('logTrips', 'currentTripID')) || null;
  try {
    if (currentTripID) {
      await loadRemoteTripIntoActiveTrip(currentTripID);
      transitionToNextRoute(ROUTE_TRIP_RESUME);
    }
  } catch (error) {
    telemetry(
      telemetry.DEBUG,
      '[logTrips.initialize] Failed to load remote trip into active trip',
    );
  }
};
